import { computed } from "vue";
import type { TableProps } from "./TableProps";
import type { TableHeaderCellProps } from "./TableHeaderCellProps";
import type { TableContext } from "./useTable.types";
import { SortDirection } from "./useTable.types";
import { applySorting, resetOtherHeaders, resolveNextDirection } from "./utils/sorting";

/**
 * Table sorting composable.
 *
 * Provides sort-by-column functionality. Clicking a sortable header cell
 * cycles through None → Ascending → Descending → None.
 *
 * Sorting is applied by rearranging DOM elements in the table body —
 * no data model or virtual DOM diffing is involved.
 *
 * @param ctx - Shared table context from `useTableContext()`.
 */
export const useTableSorting = (ctx: TableContext) => {
  const { context, stateStore, tableUid, tableProps, bodyUid, headerCells, currentColumnIndex } = ctx;

  const isCellSortable = computed(() => {
    if (context.uid === null) return false;
    const cellState = stateStore.getByUid<TableHeaderCellProps>(context.uid);
    return !!(cellState?.props?.sortable || tableProps.value?.sortable);
  });

  const sortColumn = (direction?: SortDirection) => {
    if (!isCellSortable.value || !tableProps.value?.sortable) return;
    if (!tableUid.value || context.uid === null) return;

    resetOtherHeaders(headerCells, context.uid, stateStore);

    const currentHeaderCell = stateStore.getByUid<TableHeaderCellProps>(context.uid);
    const currentDir = currentHeaderCell?.props?.sortDirection ?? SortDirection.None;
    const isSameColumn = tableProps.value.sortColumn === currentColumnIndex.value;
    const newDir = resolveNextDirection(currentDir, isSameColumn, direction);

    if (newDir === null) {
      stateStore.setValue<TableProps>(tableUid.value, "sortColumn", null);
      stateStore.setValue<TableProps>(tableUid.value, "sortDirection", SortDirection.None);
      stateStore.setValue<TableHeaderCellProps>(context.uid, "sortDirection", SortDirection.None);
      return;
    }

    stateStore.setValue<TableProps>(tableUid.value, "sortColumn", currentColumnIndex.value);
    stateStore.setValue<TableProps>(tableUid.value, "sortDirection", newDir);
    stateStore.setValue<TableHeaderCellProps>(context.uid, "sortDirection", newDir);
    applySorting(tableProps, bodyUid, stateStore);
  };

  return { isCellSortable, sortColumn };
};
